import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
  Avatar,
  IconButton,
  Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { useAuth } from '../../hooks/useAuth';
import { NAV_SECTIONS, hasAccess, isPathActive } from './navConfig';

const ROLE_LABEL = {
  admin: 'Administrator',
  manager: 'Manager',
  employee: 'Employee',
  blog_manager: 'Blog Manager',
  claims_manager: 'Claims Manager',
};

/**
 * Dark glass sidebar. `collapsed` renders the icon-only mini variant used on
 * desktop; `onNavigate` lets the mobile drawer close itself after navigation.
 */
const Sidebar = ({ collapsed = false, onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const go = (path) => {
    navigate(path);
    onNavigate?.();
  };

  const renderItem = (item) => {
    const active = isPathActive(item.path, location.pathname);

    const button = (
      <ListItemButton
        key={item.text}
        onClick={() => go(item.path)}
        aria-current={active ? 'page' : undefined}
        sx={{
          minHeight: 46,
          borderRadius: '12px',
          px: collapsed ? 1.25 : 1.75,
          justifyContent: collapsed ? 'center' : 'flex-start',
          color: active ? '#fff' : alpha('#E2E8F0', 0.72),
          position: 'relative',
          transition: 'background-color 180ms ease, color 180ms ease, transform 180ms ease',
          ...(active
            ? {
                backgroundImage: (theme) => theme.custom.gradients.brand,
                boxShadow: (theme) => theme.custom.glow,
                '&:hover': { filter: 'brightness(1.05)' },
              }
            : {
                '&:hover': {
                  backgroundColor: alpha('#E2E8F0', 0.08),
                  color: '#fff',
                },
              }),
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: collapsed ? 0 : 1.75,
            justifyContent: 'center',
            color: 'inherit',
            '& svg': { fontSize: 22 },
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: active ? 700 : 500 }}
          />
        )}
        {!collapsed && item.highlight && !active && (
          <Box
            aria-hidden
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'error.light',
              boxShadow: (theme) => `0 0 0 4px ${alpha(theme.palette.error.main, 0.25)}`,
              flexShrink: 0,
            }}
          />
        )}
      </ListItemButton>
    );

    return collapsed ? (
      <Tooltip key={item.text} title={item.text} placement="right" arrow>
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        color: '#E2E8F0',
        background: (theme) => theme.custom.gradients.night,
        overflow: 'hidden',
      }}
    >
      {/* Brand */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{
          px: collapsed ? 0 : 2.5,
          py: 2.5,
          justifyContent: collapsed ? 'center' : 'flex-start',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '13px',
            display: 'grid',
            placeItems: 'center',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src="/images/Logo_Icon-Black_Orange.png"
            alt="Skyline logo"
            sx={{ width: 28, height: 28, objectFit: 'contain' }}
          />
        </Box>
        {!collapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: '"Sora", sans-serif',
                fontWeight: 800,
                fontSize: '1.05rem',
                letterSpacing: '0.02em',
                color: '#fff',
                lineHeight: 1.2,
              }}
            >
              SKYLINE
            </Typography>
            <Typography variant="caption" sx={{ color: alpha('#E2E8F0', 0.55), letterSpacing: '0.08em' }}>
              ADMIN CONSOLE
            </Typography>
          </Box>
        )}
      </Stack>

      {/* Navigation */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 1.5,
          pb: 2,
          '&::-webkit-scrollbar-thumb': { backgroundColor: alpha('#E2E8F0', 0.2) },
        }}
      >
        {NAV_SECTIONS.map((section) => {
          const items = section.items.filter((item) => hasAccess(item, user?.role));
          if (items.length === 0) return null;
          return (
            <Box key={section.label} sx={{ mt: 1.5 }}>
              {!collapsed ? (
                <Typography
                  variant="overline"
                  sx={{
                    px: 1.75,
                    color: alpha('#E2E8F0', 0.4),
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  {section.label}
                </Typography>
              ) : (
                <Box
                  aria-hidden
                  sx={{
                    mx: 'auto',
                    my: 1.25,
                    width: 20,
                    height: 2,
                    borderRadius: 1,
                    bgcolor: alpha('#E2E8F0', 0.14),
                  }}
                />
              )}
              <List disablePadding sx={{ display: 'grid', gap: 0.5 }}>
                {items.map(renderItem)}
              </List>
            </Box>
          );
        })}
      </Box>

      {/* User card */}
      <Box
        sx={{
          m: 1.5,
          mt: 0,
          p: collapsed ? 1 : 1.5,
          borderRadius: '14px',
          backgroundColor: alpha('#E2E8F0', 0.07),
          border: `1px solid ${alpha('#E2E8F0', 0.09)}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          justifyContent: collapsed ? 'center' : 'flex-start',
          flexShrink: 0,
        }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            fontSize: '0.9rem',
            backgroundImage: (theme) => theme.custom.gradients.brand,
          }}
        >
          {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
        </Avatar>
        {!collapsed && (
          <>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography noWrap sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#fff' }}>
                {user?.name || 'User'}
              </Typography>
              <Typography noWrap variant="caption" sx={{ color: alpha('#E2E8F0', 0.55) }}>
                {ROLE_LABEL[user?.role] || user?.role || '—'}
              </Typography>
            </Box>
            <Tooltip title="Log out">
              <IconButton
                size="small"
                onClick={logout}
                aria-label="Log out"
                sx={{ color: alpha('#E2E8F0', 0.6), '&:hover': { color: '#fff' } }}
              >
                <LogoutRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Sidebar;
